/**
 * In-browser SSH key generation (Ed25519, WebCrypto) and OpenSSH encoding.
 * The private key never leaves the browser; only the public line is sent.
 */

const enc = new TextEncoder();

const u32 = (n) => new Uint8Array([(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255]);
const concat = (...parts) => {
    const out = new Uint8Array(parts.reduce((s, p) => s + p.length, 0));
    let o = 0;
    parts.forEach((p) => { out.set(p, o); o += p.length; });
    return out;
};
/** SSH wire "string": uint32 length + bytes. */
const sshString = (b) => {
    const bytes = typeof b === 'string' ? enc.encode(b) : b;
    return concat(u32(bytes.length), bytes);
};

export const toBase64 = (bytes) => {
    let s = '';
    for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
    return btoa(s);
};

/** Public key blob of an Ed25519 key. */
const ed25519Blob = (pub) => concat(sshString('ssh-ed25519'), sshString(pub));

/** "ssh-ed25519 AAAA… comment" */
export const openSshPublicKey = (pub, comment = '') => `ssh-ed25519 ${toBase64(ed25519Blob(pub))}${comment ? ` ${comment}` : ''}`;

/**
 * Unencrypted OpenSSH private key (openssh-key-v1, cipher/kdf none) for an
 * Ed25519 key. seed = 32-byte private seed, pub = 32-byte public key.
 * checkint is injectable for tests.
 */
export const openSshPrivateKey = (seed, pub, comment = '', checkint) => {
    const check = checkint ?? crypto.getRandomValues(new Uint32Array(1))[0];
    let priv = concat(
        u32(check), u32(check),
        sshString('ssh-ed25519'),
        sshString(pub),
        sshString(concat(seed, pub)),
        sshString(comment),
    );
    const pad = [];
    for (let i = 1; (priv.length + pad.length) % 8 !== 0; i++) pad.push(i);
    priv = concat(priv, new Uint8Array(pad));
    const body = concat(
        enc.encode('openssh-key-v1\0'),
        sshString('none'),
        sshString('none'),
        sshString(new Uint8Array(0)),
        u32(1),
        sshString(ed25519Blob(pub)),
        sshString(priv),
    );
    const b64 = toBase64(body).match(/.{1,70}/g).join('\n');
    return `-----BEGIN OPENSSH PRIVATE KEY-----\n${b64}\n-----END OPENSSH PRIVATE KEY-----\n`;
};

/** Ed25519 support of WebCrypto in this browser. */
export const canGenerateInBrowser = async () => {
    try {
        if (!window.crypto?.subtle) return false;
        await crypto.subtle.generateKey({ name: 'Ed25519' }, true, ['sign', 'verify']);
        return true;
    } catch (e) {
        return false;
    }
};

/** Generates an Ed25519 keypair: { publicKey (OpenSSH line), privateKey (PEM) }. Throws if unsupported. */
export const generateEd25519 = async (comment = '') => {
    const kp = await crypto.subtle.generateKey({ name: 'Ed25519' }, true, ['sign', 'verify']);
    const pub = new Uint8Array(await crypto.subtle.exportKey('raw', kp.publicKey));
    const pkcs8 = new Uint8Array(await crypto.subtle.exportKey('pkcs8', kp.privateKey));
    const seed = pkcs8.slice(pkcs8.length - 32);
    const safe = String(comment || '').replace(/[^\x21-\x7e]+/g, '-').slice(0, 64);
    return { publicKey: openSshPublicKey(pub, safe), privateKey: openSshPrivateKey(seed, pub, safe) };
};

const PUB_RE = /^(ssh-ed25519|ssh-rsa|ecdsa-sha2-nistp(256|384|521)|sk-ssh-ed25519@openssh\.com|sk-ecdsa-sha2-nistp256@openssh\.com) [A-Za-z0-9+/]{40,}={0,3}( [^\r\n]{0,100})?$/;

/** Quick client-side check of pasted text: 'ok' | 'private' | 'invalid' | 'empty'. */
export const checkPublicKeys = (text) => {
    const s = String(text || '').trim();
    if (!s) return 'empty';
    if (/PRIVATE KEY|PuTTY-User-Key-File/i.test(s)) return 'private';
    if (/^---- BEGIN SSH2 PUBLIC KEY ----/.test(s)) return 'ok'; // RFC 4716, the server converts it
    const lines = s.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    return lines.every((l) => PUB_RE.test(l)) ? 'ok' : 'invalid';
};
